import React, { useEffect, useState } from 'react';
import { api, getToken, API } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Mail, Send, Save, ShieldCheck, AlertCircle, Eye, FileText, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const empty = {
  SMTP_HOST: '', SMTP_PORT: 587, SMTP_SECURITY: 'tls',
  SMTP_USER: '', SMTP_PASSWORD: '', EMAIL_FROM: '', DEMO_EMAIL_TO: '',
};

export default function EmailConfigTab() {
  const [cfg, setCfg] = useState(empty);
  const [passwordSet, setPasswordSet] = useState(false);
  const [fallbackFichero, setFallbackFichero] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [dirty, setDirty] = useState(false);
  const [pwTouched, setPwTouched] = useState(false);

  const cargar = async () => {
    try {
      const r = await api.get('/superadmin/email-config');
      setCfg({ ...empty, ...r.data, SMTP_PASSWORD: '' });
      setPasswordSet(!!r.data.password_set);
      setFallbackFichero(!!r.data.fallback_fichero);
      setDirty(false); setPwTouched(false);
    } catch { toast.error('No se pudo cargar la configuración'); }
  };

  useEffect(() => { cargar(); }, []);

  const upd = (k, v) => { setCfg((p) => ({ ...p, [k]: v })); setDirty(true); if (k === 'SMTP_PASSWORD') setPwTouched(true); };

  const guardar = async () => {
    if (!cfg.SMTP_HOST?.trim()) { toast.error('El host SMTP es obligatorio'); return; }
    setSaving(true);
    try {
      const payload = { ...cfg, SMTP_PORT: Number(cfg.SMTP_PORT) || 587 };
      if (!pwTouched) delete payload.SMTP_PASSWORD; // no tocar contraseña si el usuario no la editó
      await api.put('/superadmin/email-config', payload);
      toast.success('Configuración guardada');
      await cargar();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo guardar');
    } finally { setSaving(false); }
  };

  const probar = async () => {
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(testEmail)) { toast.error('Email de destinatario no válido'); return; }
    setTesting(true);
    try {
      await api.post('/superadmin/email-config/test', { destinatario: testEmail });
      toast.success(`Email de prueba enviado a ${testEmail}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al enviar');
    } finally { setTesting(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <Card className="lg:col-span-2 border-zinc-200">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2 text-lg">
            <Mail className="h-5 w-5" /> Servidor de correo (SMTP)
          </CardTitle>
          <CardDescription>
            Se usa para enviar invitaciones a la app, reenvío de PIN y notificaciones de demo.
            Guardado en base de datos, se aplica a todos los entornos.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {fallbackFichero && (
            <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>Actualmente el sistema está usando el fichero <code>email_config.env</code> como respaldo. Al guardar aquí, esa configuración se sobrescribirá con la que definas en este panel.</span>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="sm:col-span-2 space-y-1.5">
              <Label>Host SMTP *</Label>
              <Input data-testid="smtp-host" placeholder="mail.tudominio.com" value={cfg.SMTP_HOST} onChange={(e) => upd('SMTP_HOST', e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Puerto</Label>
              <Input data-testid="smtp-port" type="number" min="1" max="65535" value={cfg.SMTP_PORT} onChange={(e) => upd('SMTP_PORT', e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label>Seguridad</Label>
              <Select value={cfg.SMTP_SECURITY} onValueChange={(v) => upd('SMTP_SECURITY', v)}>
                <SelectTrigger data-testid="smtp-security"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="tls">STARTTLS (587)</SelectItem>
                  <SelectItem value="ssl">SSL / TLS (465)</SelectItem>
                  <SelectItem value="none">Ninguna (25)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2 space-y-1.5">
              <Label>Usuario</Label>
              <Input data-testid="smtp-user" placeholder="noreply@tudominio.com" value={cfg.SMTP_USER} onChange={(e) => upd('SMTP_USER', e.target.value)} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Contraseña {passwordSet && !pwTouched && <span className="text-xs text-emerald-600 ml-2"><ShieldCheck className="inline h-3 w-3" /> guardada (déjala vacía para no cambiarla)</span>}</Label>
            <Input data-testid="smtp-password" type="password" placeholder={passwordSet ? '••••••••' : ''} value={cfg.SMTP_PASSWORD} onChange={(e) => upd('SMTP_PASSWORD', e.target.value)} autoComplete="new-password" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Remitente (From)</Label>
              <Input data-testid="smtp-from" placeholder="noreply@tudominio.com" value={cfg.EMAIL_FROM} onChange={(e) => upd('EMAIL_FROM', e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Destinatario de solicitudes de demo</Label>
              <Input data-testid="smtp-demo-to" placeholder="ventas@tudominio.com, otro@..." value={cfg.DEMO_EMAIL_TO} onChange={(e) => upd('DEMO_EMAIL_TO', e.target.value)} />
            </div>
          </div>
          <div className="flex justify-end pt-2">
            <Button onClick={guardar} disabled={saving || !dirty} data-testid="smtp-save">
              <Save className="h-4 w-4 mr-2" /> {saving ? 'Guardando...' : 'Guardar configuración'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="border-zinc-200 h-fit">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2 text-lg"><Send className="h-5 w-5" /> Enviar prueba</CardTitle>
          <CardDescription>Envía un email de prueba con la configuración actual guardada.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1.5">
            <Label>Email de destino</Label>
            <Input data-testid="smtp-test-email" type="email" placeholder="tu@email.com" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} />
          </div>
          <Button onClick={probar} disabled={testing || !testEmail} className="w-full" data-testid="smtp-test-send">
            <Send className="h-4 w-4 mr-2" /> {testing ? 'Enviando...' : 'Enviar email de prueba'}
          </Button>
          <p className="text-xs text-zinc-500 pt-2">
            Consejo: si tu proveedor es Plesk, usa el <em>host</em>, <em>puerto</em> y <em>credenciales</em> que aparecen en la ficha del buzón &rarr; Configurar cliente de correo.
          </p>
        </CardContent>
      </Card>

      {/* Vista previa de las plantillas de email de marketing */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-terracotta" /> Plantillas de email de marketing
          </CardTitle>
          <CardDescription>
            Estos son los correos que reciben tus clientes al registrarse y tus empleados al ser invitados.
            Pulsa &quot;Vista previa&quot; para verlos tal cual llegarán a la bandeja de entrada.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {[
            { id: 'welcome_admin', nombre: 'Bienvenida al administrador', desc: 'Se envía al registrar una empresa nueva. Presenta las novedades clave (armonización de horarios, autocierre, informes automáticos, cuadrantes).' },
            { id: 'welcome_employee', nombre: 'Bienvenida al empleado', desc: 'Se envía a un empleado al añadirlo. Explica cómo entrar y qué hacer con la validación del autocierre.' },
            { id: 'campana_2026', nombre: 'Campaña marketing 2026', desc: 'Email de captación en frío / newsletter. Destaca los diferenciales 2026 (cierre automático, 6 informes, cuadrantes, kiosko+GPS, ley 2026). Copia el HTML y pégalo en tu ESP (Mailchimp, Brevo, SendGrid…).' },
          ].map((t) => (
            <div key={t.id} className="flex flex-wrap items-center justify-between gap-3 p-4 rounded-lg border border-zinc-200 bg-zinc-50" data-testid={`plantilla-${t.id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm text-zinc-900 flex items-center gap-2">
                  <Mail className="h-4 w-4 text-terracotta shrink-0" />
                  {t.nombre}
                </div>
                <p className="text-xs text-zinc-500 mt-1">{t.desc}</p>
              </div>
              <div className="flex gap-2 shrink-0">
                <Button variant="outline" size="sm" onClick={() => {
                  const token = getToken();
                  window.open(`${API}/superadmin/emails/preview/${t.id}?formato=html&token=${token}`, '_blank');
                }} data-testid={`preview-html-${t.id}`}>
                  <Eye className="h-4 w-4 mr-1.5" /> Vista previa HTML
                </Button>
                <Button variant="outline" size="sm" onClick={() => {
                  const token = getToken();
                  window.open(`${API}/superadmin/emails/preview/${t.id}?formato=text&token=${token}`, '_blank');
                }} data-testid={`preview-text-${t.id}`}>
                  <FileText className="h-4 w-4 mr-1.5" /> Texto plano
                </Button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
